import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { Can } from '@/components/features/permission/can';
import { NoSchedulesCard } from '@/components/features/schedule/no-schedules-card';
import { ScheduleEnableSwitch } from '@/components/features/schedule/schedule-enable-switch';
import { ScheduleEnabledBadge } from '@/components/features/schedule/schedule-enabled-badge';
import ScheduleEnabledLimit from '@/components/features/schedule/schedule-enabled-limit';
import { ScheduleModeBadge } from '@/components/features/schedule/schedule-mode-badge';
import { FeaturedScheduleCard } from '@/components/features/schedule/template-card';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderAction,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { featuredScheduleTemplates } from '@/config/schedule-templates';
import { getOrganizationSchedulesQuery } from '@/queries/getOrganizationSchedulesQuery';

export const Route = createFileRoute('/_authenticated/schedule/')({
  component: RouteComponent,
});

const LoadingSchedules = () => {
  return (
    <TableRow>
      <TableCell colSpan={5} className="text-center">
        Loading...
      </TableCell>
    </TableRow>
  );
};

function RouteComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const organization = useCurrentOrganization();

  const { data: schedules, isLoading } = useQuery(
    getOrganizationSchedulesQuery(organization.organizationId)
  );

  if (!isLoading && schedules.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh)]">
        <NoSchedulesCard
          onCreateSchedule={() => {
            navigate({ to: '/schedule/create' });
          }}
        />
      </div>
    );
  }

  return (
    <ScheduleEnabledLimit organizationId={organization.organizationId}>
      {() => (
        <Page>
          <PageHeader>
            <PageHeaderInfo>
              <div className="flex items-center gap-3">
                <PageHeaderTitle>
                  {t('authenticated.schedule.list.title')}
                </PageHeaderTitle>
                <ScheduleEnabledLimit.Badge />
              </div>
              <PageHeaderDescription>
                {t('authenticated.schedule.list.description')}
              </PageHeaderDescription>
            </PageHeaderInfo>

            <PageHeaderAction>
              <Can permissions={['schedule:create']}>
                <Button
                  onClick={() => navigate({ to: '/schedule/create' })}
                  icon={<PlusIcon />}
                >
                  {t('authenticated.schedule.list.createSchedule')}
                </Button>
              </Can>
            </PageHeaderAction>
          </PageHeader>

          <PageContent>
            <Card>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">
                        {t('authenticated.schedule.list.table.name')}
                      </TableHead>
                      <TableHead className="w-full">
                        {t('authenticated.schedule.list.table.description')}
                      </TableHead>
                      <TableHead className="w-[100px]">
                        {t('authenticated.schedule.list.table.type')}
                      </TableHead>
                      <TableHead className="w-[100px]">
                        {t('authenticated.schedule.list.table.status')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>

                  {isLoading && (
                    <TableBody>
                      <LoadingSchedules />
                    </TableBody>
                  )}

                  {!isLoading && (
                    <TableBody>
                      {schedules.map((schedule) => (
                        <TableRow
                          key={schedule.scheduleId}
                          className="cursor-pointer"
                          onClick={() => {
                            navigate({
                              to: '/schedule/$scheduleId/details',
                              params: { scheduleId: schedule.scheduleId },
                            });
                          }}
                        >
                          <TableCell className="font-bold">
                            {schedule.name}
                          </TableCell>
                          <TableCell>{schedule.description || '-'}</TableCell>
                          <TableCell>
                            <ScheduleModeBadge mode={schedule.mode} />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <ScheduleEnabledBadge schedule={schedule} />
                              <ScheduleEnableSwitch schedule={schedule} />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  )}
                </Table>
              </CardContent>
            </Card>

            {/* Featured Templates Section */}
            <div className="pt-4">
              <h2 className="text-xl font-semibold mb-4">
                {t('authenticated.schedule.list.templatesTitle')}
              </h2>
              <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {featuredScheduleTemplates(t).map((template) => (
                  <FeaturedScheduleCard
                    key={template.name}
                    name={template.name}
                    description={template.description}
                    mode={template.mode}
                    color={template.color}
                    features={template.features}
                  />
                ))}
              </div>
            </div>
          </PageContent>
        </Page>
      )}
    </ScheduleEnabledLimit>
  );
}
